// Alignment-Matrix ILO×AT (Designkonzept §5.4): einklappbare Sektion unter
// dem Workspace, Daten direkt aus dem neuesten AnalysisReport
// (alignment_matrix.cells + coverage). Zellen-Klick → Fokus auf das Paar
// folgt in Slice 2.

import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { AnalysisReport, AT, ILO } from "@/api/client";

interface MatrixCellData {
  ilo_id: string;
  at_id: string;
  bloom_distance: number | null;
  severity: "ok" | "tension" | "misalignment";
}

interface MatrixCoverage {
  unassessed_ilo_ids?: string[];
  orphan_at_ids?: string[];
}

function parseMatrix(report: AnalysisReport): {
  cells: MatrixCellData[];
  coverage: MatrixCoverage;
} {
  const raw = report.alignment_matrix as {
    cells?: MatrixCellData[];
    coverage?: MatrixCoverage;
  };
  return { cells: raw?.cells ?? [], coverage: raw?.coverage ?? {} };
}

// Farblogik: aligned (ok) leuchtet blau je nach Distanz, tension = Amber,
// misalignment = Rot. „—" (nicht verknüpft) bleibt neutral grau (§2:
// kein Grün-Ampel-Denken).
function cellClass(cell: MatrixCellData | undefined): string {
  if (!cell) return "text-ws-surface-muted";
  if (cell.severity === "misalignment")
    return "bg-[rgba(255,92,92,.16)] text-verdict-red";
  if (cell.severity === "tension")
    return "bg-[rgba(245,166,35,.14)] text-verdict-amber";
  if (cell.bloom_distance === 0)
    return "bg-[rgba(77,124,254,.28)] text-ca-surface-accent";
  return "bg-[rgba(77,124,254,.10)] text-ws-surface-ink";
}

export default function AlignmentMatrixSection({
  report,
  ilos,
  ats,
  onPairFocus,
}: {
  report: AnalysisReport | null;
  ilos: ILO[];
  ats: AT[];
  // Zellen-Klick: highlightet das ILO-AT-Paar in den Spalten (Konzept §5.4).
  onPairFocus: (iloId: string, atId: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { cells, coverage } = report
    ? parseMatrix(report)
    : { cells: [], coverage: {} };
  const cellByPair = new Map(cells.map((c) => [`${c.ilo_id}|${c.at_id}`, c]));
  const unassessed = new Set(coverage.unassessed_ilo_ids ?? []);

  return (
    <section className="mx-6 mb-7 mt-1 overflow-hidden rounded-xl border border-ws-surface-border bg-ws-card text-ws-surface-ink">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 text-left transition hover:bg-black/[0.03]"
        style={{ padding: "12px 18px" }}
      >
        <span
          className={`text-xs text-ws-surface-muted transition-transform duration-200 ${
            open ? "rotate-90" : ""
          }`}
        >
          ▶
        </span>
        <h2 className="text-[13.5px] font-semibold tracking-[-0.01em]">
          {t("workspace.matrix.title")}
        </h2>
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ws-surface-muted">
          {report ? t("workspace.matrix.fromReport") : t("workspace.matrix.noReport")}
        </span>
      </button>
      {open && (
        <div style={{ padding: "0 18px 18px" }}>
          {report && ilos.length > 0 && ats.length > 0 ? (
            <>
              <table className="w-full max-w-[720px] border-collapse">
                <thead>
                  <tr>
                    <th className="border border-ws-surface-border bg-white p-2 text-[12.5px] font-semibold text-ws-surface-muted" />
                    {ats.map((at, i) => (
                      <th
                        key={at.id}
                        className="border border-ws-surface-border bg-white p-2 text-center text-[12.5px] font-semibold text-ws-surface-muted"
                      >
                        AT {i + 1}
                        {at.bloom_levels?.length
                          ? ` (K${Math.max(...at.bloom_levels)})`
                          : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ilos.map((ilo, i) => (
                    <tr key={ilo.id}>
                      <th className="border border-ws-surface-border bg-white p-2 text-left text-[12.5px] font-semibold text-ws-surface-muted">
                        ILO {i + 1}
                        {ilo.bloom_level != null ? ` (K${ilo.bloom_level})` : ""}
                      </th>
                      {ats.map((at) => {
                        const cell = cellByPair.get(`${ilo.id}|${at.id}`);
                        return (
                          <td
                            key={at.id}
                            onClick={() => onPairFocus(ilo.id, at.id)}
                            title={t("workspace.matrix.cellHint")}
                            className={`cursor-pointer border border-ws-surface-border p-2 text-center text-[12.5px] font-semibold ${cellClass(cell)}`}
                          >
                            {cell ? (cell.bloom_distance ?? "!") : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2.5 flex flex-wrap gap-4 text-xs text-ws-surface-muted">
                <span>
                  <Swatch className="bg-[rgba(77,124,254,.5)]" />
                  {t("workspace.matrix.legendAligned")}
                </span>
                <span>
                  <Swatch className="bg-[rgba(245,166,35,.5)]" />
                  {t("workspace.matrix.legendTension")}
                </span>
                <span>
                  <Swatch className="bg-[rgba(255,92,92,.5)]" />
                  {t("workspace.matrix.legendMisalignment")}
                </span>
                <span>
                  <Swatch className="border border-ws-surface-border bg-white" />
                  {t("workspace.matrix.legendUnlinked")}
                </span>
                {ilos
                  .map((ilo, i) => ({ ilo, n: i + 1 }))
                  .filter(({ ilo }) => unassessed.has(ilo.id))
                  .map(({ n }) => (
                    <span key={n} className="text-verdict-amber">
                      {t("workspace.matrix.orphanRow", { number: n })}
                    </span>
                  ))}
              </div>
            </>
          ) : (
            <p className="text-[13px] text-ws-surface-muted">
              {t("workspace.matrix.emptyHint")}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function Swatch({ className }: { className: string }) {
  return (
    <span
      className={`mr-1.5 inline-block h-2.5 w-2.5 rounded-[3px] align-[-1px] ${className}`}
      aria-hidden
    />
  );
}
